import {RxdbRepository} from '../rxdb.repository';
import {replicateCouchDB, RxCouchDBReplicationState} from 'rxdb/plugins/replication-couchdb';
import {environment} from '../../../../environments/environment';
import {Inject, Injectable} from '@angular/core';
import {OKTA_AUTH} from '@okta/okta-angular';
import {OktaAuth} from '@okta/okta-auth-js';
import {Store} from '@ngrx/store';
import {UpdateCollectionSyncStatusAction} from '../../store/standalone';
import {RXDB_CMC_PROTOCOL_SCHEMA} from '../schemas/rxdb-cmc-protocol.schema';
import {markPulled, shouldPull, withTabLock} from './multi-tab-sync';

const COUCHDB_REPLICATION_TIMEOUT_MS = 5 * 60_000;


@Injectable({
    providedIn: 'root'
})
export class CouchdbCollectionsSynchronizer {

    private static LIVE_COUCHDB_COLLECTIONS = [
        {name: 'cmcprotocols', schema: RXDB_CMC_PROTOCOL_SCHEMA}
    ]

    private countryIsoCode;

    public constructor(private rxdbRepository: RxdbRepository,
                       @Inject(OKTA_AUTH) private _oktaAuth: OktaAuth, private store: Store) {
    }

    private async couchDbDatabaseExists(collectionName: string): Promise<boolean> {
        const authToken = this._oktaAuth.getAccessToken();
        if (!authToken) {
            throw new Error('Missing auth token');
        }
        const url = environment.synchronizerService.url + "v1/couch-db/" + collectionName + '/';
        const res = await fetch(url, {
            method: 'HEAD',
            headers: {Authorization: `Bearer ${authToken}`}
        });
        if (res.status === 404) {
            return false;
        }
        if (res.ok) {
            return true;
        }
        const error = new Error(`HTTP error ${res.status} for ${url}`);
        (error as any).status = res.status;
        throw error;
    }

    async synchronizeCollections(isFirstTimeSyncDone: boolean, countryIsoCode: string): Promise<void> {
        countryIsoCode = countryIsoCode;
        const collections= CouchdbCollectionsSynchronizer.LIVE_COUCHDB_COLLECTIONS;
        const maxParallelHttpRequests = 2;

        for (let i = 0; i < collections.length; i += maxParallelHttpRequests) {
            const collectionsToReplicate = collections.slice(i, i + maxParallelHttpRequests);
            await Promise.all(collectionsToReplicate.map(async collectionToReplicate => {
                const collectionName: string = `${countryIsoCode}_${collectionToReplicate.name}`;
                await this.replicateCollection(collectionName, !isFirstTimeSyncDone);
            }));
        }
    }

    /**
     * Réplique une collection depuis CouchDB, un seul onglet à la fois (pas de dépendance à l'onglet leader).
     * mustWait (premier sync) : attendre la fin d'une réplication en cours dans un autre onglet et ignorer le throttle ;
     * sinon la réplication est sautée si un autre onglet s'en occupe ou vient de la faire.
     */
    private async replicateCollection(collectionName: string, mustWait: boolean): Promise<void> {
        await withTabLock(`epp-couchdb-${collectionName}`, async () => {
            if (!mustWait && !shouldPull(collectionName)) {
                return;
            }
            const replicationState = await this.syncWithCouchDB(collectionName, 20);
            if (!replicationState) {
                return;
            }
            // live: false → la réplication s'annule d'elle-même une fois à jour ; on l'attend pour garder le lock
            // pendant toute sa durée, avec un garde-fou si CouchDB ne répond plus (retry infini sinon)
            let timer: ReturnType<typeof setTimeout>;
            const completed = await Promise.race([
                replicationState.awaitInitialReplication().then(() => true),
                new Promise<boolean>(resolve => timer = setTimeout(() => resolve(false), COUCHDB_REPLICATION_TIMEOUT_MS))
            ]).finally(() => clearTimeout(timer));
            if (!completed) {
                console.warn('CouchDB [' + collectionName + '] replication timed out, cancelling');
                await replicationState.cancel();
                return;
            }
            markPulled(collectionName);
        }, {ifAvailable: !mustWait});
    }

    async syncWithCouchDB(collectionName: string, batchsize: number = 500): Promise<RxCouchDBReplicationState<any> | undefined> {
        const oktaAuth = this._oktaAuth;
        const db = await this.rxdbRepository.initDb();


        try {
            const exists = await this.couchDbDatabaseExists(collectionName);
            if (!exists) {
                console.warn('CouchDB [' + collectionName + '] does not exist remotely yet, skipping replication');
                return undefined;
            }

            const fetchWithTimeoutAndAuth = async (url: string, options: RequestInit = {}) => {
                const controller = new AbortController();

                const authToken = oktaAuth.getAccessToken();
                if (!authToken) {
                    throw new Error('Missing auth token');
                }

                const headers = new Headers(options.headers || {});
                headers.set('Authorization', `Bearer ${authToken}`);


                const waitForIdle = () => new Promise<void>(resolve => {
                    const pause = 200;
                    if ('requestIdleCallback' in window) {
                        requestIdleCallback(() => resolve(), {timeout: pause});
                    } else {
                        setTimeout(resolve, pause);
                    }
                });

                if (url.includes('_changes')) {
                    await waitForIdle();
                }

                try {
                    const res = await fetch(url, {
                        ...options,
                        headers,
                        signal: controller.signal,
                    });

                    if (!res.ok) {
                        const error = new Error(`HTTP error ${res.status} for ${url}`);
                        (error as any).status = res.status;
                        (error as any).url = url;
                        throw error;
                    }
                    return res;
                } catch (error) {
                    throw error;
                }
            };

            const replicationState = this.createReplication({
                replicationIdentifier: collectionName + '_couchdb_replication',
                // la réplication démarre dans l'onglet appelant ; la sérialisation entre onglets est faite par replicateCollection
                waitForLeadership: false,
                collection: db[collectionName],
                url: environment.synchronizerService.url + "v1/couch-db/" + collectionName + '/',
                fetch: fetchWithTimeoutAndAuth,
                live: false,
                retryTime: 15000,
                deletedField: '_deleted',
                pull: {
                    batchSize: 500,
                    heartbeat: 10000
                }
            });
            replicationState.error$.subscribe((err) => {
                // console.error(err);
            });
            replicationState.active$.subscribe((isActive) => {
               this.store.dispatch(new UpdateCollectionSyncStatusAction(collectionName, isActive ? 'IN_PROGRESS':'DONE', new Date()));
            });
            return replicationState;
        } catch (error) {
            console.warn('CouchDB [' + collectionName + '] error while synchronisation :', error.message);
        }
    }

    createReplication(options: Parameters<typeof replicateCouchDB>[0]): RxCouchDBReplicationState<any> {
        return replicateCouchDB(options);
    }

}
